// icicleTour.js — the timeline's guided tour (Shepherd.js), mirroring sosTour.js.
//
// Auto-runs once on first visit (localStorage gate), always re-launchable from the orientation
// card's "Take the quick tour". Steps attach only to targets present on the page, so the tour
// never points at nothing (e.g. the chat pill is absent while the chat is open).
//
// Targets (stable ids in Icicle.js): #ic-map, #sos-legend (shared SunburstLegend id),
// #ic-metric-select, #ic-pane, #ic-modes (the reading-mode tabs), #ic-chat-pill.

import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

const SEEN_KEY = 'ic_tour_seen';

export function shouldAutoStartIcicleTour() {
  try { return !window.localStorage.getItem(SEEN_KEY); } catch (e) { return false; }
}

function markSeen() {
  try { window.localStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* ignore */ }
}

// Build (and return) a fresh tour. onDone(status) fires on 'complete' or 'cancel'.
export function buildIcicleTour({ onDone } = {}) {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    exitOnEsc: true,
    defaultStepOptions: {
      classes: 'sos-shepherd',   // same navy theme as the wheel tour (rules inlined in Icicle.js)
      scrollTo: { behavior: 'smooth', block: 'center' },
      cancelIcon: { enabled: true },
      arrow: true,
    },
  });

  const back = { text: 'Back', action: () => tour.back(), secondary: true };
  const next = { text: 'Next', action: () => tour.next() };
  const skip = { text: 'Skip', action: () => tour.cancel(), secondary: true };
  const done = { text: 'Done', action: () => tour.complete() };

  const specs = [
    {
      sel: '#ic-map', on: 'bottom',
      title: 'Your script, one line',
      text: 'This map is your whole screenplay in story order, left to right. A unit’s width is its length in pages — wider means more of your script.',
    },
    {
      sel: '#sos-legend', on: 'bottom',
      title: 'Colour = the triage call',
      text: 'You start on <strong>Triage</strong> — a recommendation for each part: Keep, Polish, Rework, or Cut&nbsp;/&nbsp;rebuild. It points you to where to look first.',
    },
    {
      sel: '#ic-metric-select', on: 'bottom',
      title: 'Or recolour by one craft signal',
      text: 'Use <strong>Colour by</strong> to repaint the map by a single signal — Design, Execution, Read, or any of the twelve axes. Now colour is that signal’s <strong>score</strong>: red (needs work) to green (strong), grey where it isn’t owed.',
    },
    {
      sel: '#ic-pane', on: 'top',
      title: 'Click anything in the map',
      text: 'Every act, sequence and scene has its own reading — the grade, what’s dragging it, and how to lift it. Click a cell above and it opens here.',
    },
    {
      sel: '#ic-modes', on: 'bottom',
      title: 'Three ways to read it',
      text: '<strong>Findings</strong> is the whole-script case, most important first. <strong>Axes</strong> lists the twelve craft signals — scan them and drill into any one. <strong>Patterns</strong> shows what repeats at each level.',
    },
    {
      sel: '#ic-chat-pill', on: 'top',
      title: 'Ask anything',
      text: 'The chat knows the whole read — ask where to start, or why a note fired. That’s the tour — dive in.',
    },
  ];

  const present = specs.filter((s) => document.querySelector(s.sel));
  present.forEach((s, i) => {
    const first = i === 0;
    const last = i === present.length - 1;
    tour.addStep({
      id: 'ic-' + i,
      title: s.title,
      text: s.text,
      attachTo: { element: s.sel, on: s.on },
      buttons: last ? [back, done] : (first ? [skip, next] : [back, next]),
    });
  });

  tour.on('complete', () => { markSeen(); if (onDone) onDone('complete'); });
  tour.on('cancel', () => { markSeen(); if (onDone) onDone('cancel'); });
  return tour;
}
